/**
 * React Query hooks for Employee Type operations.
 */

import { useQuery, keepPreviousData } from '@tanstack/react-query';
import employeeTypeService from '../services/employee-type.service';

interface UseEmployeeTypeListQueryParams {
  workspaceId?: string;
  idToken: string | undefined;
  page?: number;
  size?: number;
  sortBy?: string;
  order?: 'asc' | 'desc';
  search?: string;
  enabled?: boolean;
  refetchInterval?: number | false;
}

export const useEmployeeTypeListQuery = ({
  workspaceId,
  idToken,
  page = 1,
  size = 50,
  sortBy = 'name',
  order = 'asc',
  search,
  enabled = true,
  refetchInterval = 5000,
}: UseEmployeeTypeListQueryParams) => {
  return useQuery({
    queryKey: ['employee-types', workspaceId, page, size, sortBy, order, search],
    queryFn: async () => {
      if (!workspaceId || !idToken) {
        throw new Error('Workspace ID and token are required');
      }

      return await employeeTypeService.listEmployeeTypes(workspaceId, idToken, {
        page,
        size,
        sortBy,
        order,
        search,
      });
    },
    enabled: enabled && !!workspaceId && !!idToken,
    placeholderData: keepPreviousData,
    refetchInterval,
    refetchIntervalInBackground: true,
    staleTime: 2000,
    gcTime: 10 * 60 * 1000,
  });
};

interface UseEmployeeTypeDetailQueryParams {
  employeeTypeId: string | undefined;
  workspaceId?: string;
  idToken: string | undefined;
  enabled?: boolean;
}

export const useEmployeeTypeDetailQuery = ({
  employeeTypeId,
  workspaceId,
  idToken,
  enabled = true,
}: UseEmployeeTypeDetailQueryParams) => {
  return useQuery({
    queryKey: ['employee-type', workspaceId, employeeTypeId],
    queryFn: async () => {
      if (!employeeTypeId || !workspaceId || !idToken) {
        throw new Error('Employee type ID, workspace ID, and token are required');
      }

      return await employeeTypeService.getEmployeeType(workspaceId, employeeTypeId, idToken);
    },
    enabled: enabled && !!workspaceId && !!employeeTypeId && !!idToken,
    staleTime: 30000,
    gcTime: 10 * 60 * 1000,
  });
};
